/**
 * SEOHead Component
 * Universal SEO component for dynamic meta tag injection
 * Handles title, meta description, Open Graph, Twitter Cards, and JSON-LD
 * 
 * @module components/seo/SEOHead
 */

import React, { useEffect } from 'react';

export interface SEOProps {
    /** Page title (will be suffixed with brand) */
    title: string;
    /** Meta description (optimal: 150-160 chars) */
    description: string;
    /** Canonical URL for this page */
    canonical?: string;
    /** Open Graph image URL (1200x630 recommended) */
    ogImage?: string;
    /** Open Graph type */
    ogType?: 'website' | 'article' | 'product';
    /** Set to true for pages that shouldn't be indexed */
    noIndex?: boolean;
    /** Structured data objects (JSON-LD) */
    structuredData?: object | object[];
    /** Article-specific metadata for blog posts */
    article?: {
        publishedTime: string;
        modifiedTime?: string;
        author: string;
        section?: string;
        tags?: string[];
    };
}

// Brand name for title suffix
// Brand name for title suffix
const BRAND_NAME = 'FuturaSmile';
// Dynamic base URL (client-side) or fallback to production domain
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://futurasmile.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-image.jpg`;

/**
 * SEOHead - Dynamically sets page meta tags and structured data
 * 
 * @example
 * <SEOHead
 *   title="Pricing - Affordable Credit Packs"
 *   description="Buy credits for AI smile simulations. Starting at $15."
 *   // canonical prop is optional - defaults to current URL if omitted
 *   structuredData={productSchema}
 * />
 */
const SEOHead: React.FC<SEOProps> = ({
    title,
    description,
    canonical,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    noIndex = false,
    structuredData,
    article,
}) => {
    useEffect(() => {
        // Helper to set/update meta tags
        const setMeta = (name: string, content: string, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;

            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute(attr, name);
                document.head.appendChild(meta);
            }
            meta.content = content;
        };

        // Helper to set link tags
        const setLink = (rel: string, href: string) => {
            let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

            if (!link) {
                link = document.createElement('link');
                link.rel = rel;
                document.head.appendChild(link);
            }
            link.href = href;
        };

        // Helper to create/update JSON-LD script
        const setJsonLd = (data: object | object[]) => {
            // Remove existing JSON-LD scripts (except Organization schema in index.html)
            const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-head]');
            existingScripts.forEach(script => script.remove());

            // Handle array of schemas or single schema
            const schemas = Array.isArray(data) ? data : [data];

            schemas.forEach((schema, index) => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-seo-head', `schema-${index}`);
                script.textContent = JSON.stringify(schema);
                document.head.appendChild(script);
            });
        };

        // Format title with brand suffix
        const fullTitle = title.includes(BRAND_NAME)
            ? title
            : `${title} | ${BRAND_NAME}`;

        // Update document title
        document.title = fullTitle;

        // Basic meta tags
        setMeta('description', description);
        setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

        // Open Graph tags
        setMeta('og:title', fullTitle, true);
        setMeta('og:description', description, true);
        setMeta('og:type', ogType, true);
        setMeta('og:image', ogImage, true);


        // Determine canonical URL (default to current page if not provided)
        const effectiveCanonical = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');

        if (effectiveCanonical) {
            setMeta('og:url', effectiveCanonical, true);
            setLink('canonical', effectiveCanonical);
        }

        // Twitter Card tags
        setMeta('twitter:title', fullTitle);
        setMeta('twitter:description', description);
        setMeta('twitter:image', ogImage);

        // Article-specific meta (for blog posts)
        if (article) {
            setMeta('og:type', 'article', true);
            setMeta('article:published_time', article.publishedTime, true);
            if (article.modifiedTime) {
                setMeta('article:modified_time', article.modifiedTime, true);
            }
            setMeta('article:author', article.author, true);
            if (article.section) {
                setMeta('article:section', article.section, true);
            }
            if (article.tags) {
                article.tags.forEach(tag => {
                    setMeta('article:tag', tag, true);
                });
            }
        }

        // Structured data (JSON-LD)
        if (structuredData) {
            setJsonLd(structuredData);
        }

        // Cleanup function
        return () => {
            // Remove JSON-LD scripts added by this component
            const scripts = document.querySelectorAll('script[data-seo-head]');
            scripts.forEach(script => script.remove());
        };
    }, [title, description, canonical, ogImage, ogType, noIndex, structuredData, article]);

    // This component doesn't render anything visible
    return null;
};

export default SEOHead;
